import { Logger } from '../support/Logger';
import { ILogger } from '../support/ILogger';

// 'slow' is a picture still coming after the visible timeout; 'failed' is one that will not come.
export type IAvatarImageStatus = 'loading' | 'slow' | 'loaded' | 'failed';

/**
 * Loading is injected rather than called directly so the state machine can be exercised
 * without a DOM: `new Image()` does not exist under the unit test runner.
 */
export interface IImageLoader {
    load: (url: string) => Promise<void>;
}

/**
 * Loads off-document, so a URL that fails never reaches the DOM and the browser has nothing
 * to draw a broken-image indicator into.
 */
export class BrowserImageLoader implements IImageLoader {
    load = (url: string): Promise<void> => {
        return new Promise((resolve, reject) => {
            const image = new Image();
            // Google's photo host intermittently refuses http://localhost, a referrer our Android
            // app shares with many others. Google's photos are cacheable, so the visible element
            // paints from what this fetched instead of asking again.
            image.referrerPolicy = 'no-referrer';
            image.onload = () => resolve();
            image.onerror = () => reject(new Error(`could not load ${url}`));
            image.src = url;
        });
    };
}

export interface IAvatarImageRequest {
    url: string;
    onStatus: (status: IAvatarImageStatus) => void;
    visibleTimeoutMs?: number;
    hardTimeoutMs?: number;
    retryDelaysMs?: number[];
}

/**
 * What became of one attempt. A url that fails intermittently looks the same in a report either
 * way, so the report carries which it was: 'error' means the request was refused or the image was
 * unusable, 'timeout' means nothing had arrived by the hard timeout and the request was abandoned.
 */
export interface IAvatarLoadAttempt {
    outcome: 'loaded' | 'timeout' | 'error';
    elapsedMs: number;
}

// How long an image is 'loading' before it is 'slow', which callers draw as the placeholder. The
// request carries on, and a photo that arrives later replaces the placeholder.
const DEFAULT_VISIBLE_TIMEOUT_MS = 3000;
// A request still unanswered by then is assumed lost, so it is abandoned and a fresh one is made.
const DEFAULT_HARD_TIMEOUT_MS = 30000;
const DEFAULT_RETRY_DELAYS_MS = [1000, 3000];

interface IAttemptRequest {
    url: string;
    visibleTimeoutMs: number;
    hardTimeoutMs: number;
    onVisibleTimeout: () => void;
}

export class AvatarImageLoader {
    static inject = () => [BrowserImageLoader, Logger];
    constructor(private imageLoader: IImageLoader, private logger: ILogger) {}

    // An image that fails is usually drawn wherever its person appears, so each is reported once.
    private reportedFailures = new Set<string>();

    /**
     * Reports 'loading' immediately, 'slow' if the first attempt outlasts the visible timeout,
     * 'failed' once an attempt errors or reaches the hard timeout, and 'loaded' whenever an attempt
     * succeeds, including after 'slow' or 'failed'. Only an error or the hard timeout starts another
     * attempt, after the next retry delay, and only the last attempt failing is reported.
     *
     * Returns a cancel function that must be called on unmount and whenever the url changes, or
     * a late result from a cancelled load will overwrite newer state.
     */
    load = ({
        url,
        onStatus,
        visibleTimeoutMs = DEFAULT_VISIBLE_TIMEOUT_MS,
        hardTimeoutMs = DEFAULT_HARD_TIMEOUT_MS,
        retryDelaysMs = DEFAULT_RETRY_DELAYS_MS
    }: IAvatarImageRequest): (() => void) => {
        let cancelled = false;
        let shown: IAvatarImageStatus | undefined;

        // Every failed attempt ends in 'failed', and only the first is passed on.
        const emit = (status: IAvatarImageStatus) => {
            if (!cancelled && status !== shown) {
                shown = status;
                onStatus(status);
            }
        };

        emit('loading');

        const attempts: IAvatarLoadAttempt[] = [];

        const attempt = async (remainingDelays: number[]): Promise<void> => {
            const result = await this.tryOnce({
                url,
                visibleTimeoutMs,
                hardTimeoutMs,
                // a retry's visible timeout must not turn a 'failed' back into 'slow'
                onVisibleTimeout: () => {
                    if (shown === 'loading') {
                        emit('slow');
                    }
                }
            });
            attempts.push(result);
            if (cancelled) {
                return;
            }
            if (result.outcome === 'loaded') {
                emit('loaded');
                return;
            }

            emit('failed');

            if (remainingDelays.length === 0) {
                this.reportFailure(url, attempts);
                return;
            }

            const [delay, ...rest] = remainingDelays;
            await this.wait(delay);
            if (cancelled) {
                return;
            }
            return attempt(rest);
        };

        // Deliberately unguarded. tryOnce turns a rejected load into an attempt record, so attempt
        // only rejects if onStatus itself throws -- a defect in the caller, which should surface
        // rather than be reported back through the same throwing callback.
        attempt(retryDelaysMs);

        return () => {
            cancelled = true;
        };
    };

    /**
     * One request, awaited until it settles or reaches the hard timeout. A request that never
     * answers would otherwise hold the attempt open forever, and no retry would ever start.
     */
    private tryOnce = async ({ url, visibleTimeoutMs, hardTimeoutMs, onVisibleTimeout }: IAttemptRequest): Promise<IAvatarLoadAttempt> => {
        // Monotonic, so a clock correction mid-load cannot report a duration that never happened.
        const startedAt = performance.now();
        const visibleTimer = setTimeout(onVisibleTimeout, visibleTimeoutMs);
        let hardTimer: ReturnType<typeof setTimeout> | undefined;

        const timedOut = new Promise<IAvatarLoadAttempt['outcome']>(resolve => {
            hardTimer = setTimeout(() => resolve('timeout'), hardTimeoutMs);
        });
        const settled = (async (): Promise<IAvatarLoadAttempt['outcome']> => {
            try {
                await this.imageLoader.load(url);
                return 'loaded';
            }
            catch {
                // Dropped deliberately: the message embeds the url with its media token, and which
                // of the two cases this was is the only part worth reporting.
                return 'error';
            }
        })();

        const outcome = await Promise.race([settled, timedOut]);
        clearTimeout(visibleTimer);
        if (hardTimer) {
            clearTimeout(hardTimer);
        }
        return { outcome, elapsedMs: Math.round(performance.now() - startedAt) };
    };

    // Logger sends nothing while the device is offline, when every image would fail. Without the
    // query string, which can carry a media token.
    private reportFailure = (url: string, attempts: IAvatarLoadAttempt[]) => {
        // a local preview carries the picture itself
        if (/^(data|blob):/i.test(url)) {
            return;
        }
        const location = url.split('?')[0];
        if (this.reportedFailures.has(location)) {
            return;
        }
        this.reportedFailures.add(location);
        this.logger.error(new Error('avatar image could not be loaded'), { url: location, attempts });
    };

    private wait = (ms: number): Promise<void> => {
        return new Promise(resolve => setTimeout(resolve, ms));
    };
}
